import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, Pause } from 'lucide-react';
import { POWER_STATE } from '../../../lib/device/catalog';
import type { PowerState } from '../../../lib/device/catalog';
import { TRAVEL } from '../../../lib/rcu/catalog/protocol';
import type { TravelKind } from '../../../lib/rcu/catalog/protocol';

export interface CurtainControlProps {
  id: string;
  name: string;
  subtext?: string;
  icon: React.ReactNode;
  status: PowerState;
  isOnline: boolean;
  onToggle: (id: string) => void;
  /** Motors are told which way to run; nothing reports where they are. */
  onTravel: (id: string, travel: TravelKind) => void;
  className?: string;
}

/**
 * Curtain control: open, stop, close.
 *
 * There is no position bar because the DIN curtain modules in use take a
 * direction rather than a target position — a slider would be showing the guest
 * a value the hardware never receives.
 */
export const CurtainControl: React.FC<CurtainControlProps> = ({
  id,
  name,
  subtext,
  icon,
  status,
  isOnline,
  onToggle,
  onTravel,
  className = '',
}) => {
  const isOpen = status === POWER_STATE.ON;

  const handleOpen = (): void => {
    if (!isOnline) return;
    onTravel(id, TRAVEL.OPEN);
    if (!isOpen) onToggle(id);
  };

  const handleClose = (): void => {
    if (!isOnline) return;
    onTravel(id, TRAVEL.CLOSE);
    if (isOpen) onToggle(id);
  };

  const buttonClass = (): string =>
    `bg-white text-[#333333] shadow-sm hover:bg-[#FDF6E9] transition-all duration-200`;
  const disabledButtonClass = (): string =>
    `bg-[#F0F0F0] text-[#999999] cursor-not-allowed`;

  return (
    <motion.div
      className={`
        bg-[#F8F8F8] rounded-2xl p-4 
        ${!isOnline ? 'opacity-50' : ''}
        ${className}
      `}
      whileHover={isOnline ? { y: -2 } : undefined}
      transition={{ duration: 0.2 }}
    >
      {/* Top Row - Icon, Name, and Status */}
      <div className="flex items-center gap-4 mb-4">
        {/* Icon Container */}
        <div
          className={`
            w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0
            ${isOpen ? 'bg-[#D4A054]' : 'bg-[#FDF6E9]'}
            transition-colors duration-300
          `}
        >
          <div
            className={`w-6 h-6 flex items-center justify-center ${
              isOpen ? 'text-white' : 'text-[#D4A054]'
            }`}
          >
            {icon}
          </div>
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-[#333333] text-base font-medium truncate">{name}</h3>
          <p className={`text-sm ${isOpen ? 'text-[#D4A054]' : 'text-[#666666]'}`}>
            {isOpen ? 'Open' : 'Closed'}
            {!isOnline && ' (Offline)'}
          </p>
          {subtext !== undefined && subtext !== '' && (
            <p className="text-xs text-[#999999] truncate">{subtext}</p>
          )}
        </div>
      </div>

      {/* Direction buttons: the module has no absolute position to send. */}
      <div className="flex gap-2">
        <button
          onClick={handleOpen}
          disabled={!isOnline}
          className={`flex-1 py-3 px-3 rounded-lg flex items-center justify-center gap-1 ${
            isOnline ? buttonClass() : disabledButtonClass()
          }`}
        >
          <ChevronUp className="w-4 h-4" />
          <span className="text-sm font-medium">Open</span>
        </button>

        <button
          onClick={() => isOnline && onTravel(id, TRAVEL.STOP)}
          disabled={!isOnline}
          aria-label="Stop"
          className={`px-3 py-3 rounded-lg flex items-center justify-center ${
            isOnline ? buttonClass() : disabledButtonClass()
          }`}
        >
          <Pause className="w-4 h-4" />
        </button>

        <button
          onClick={handleClose}
          disabled={!isOnline}
          className={`flex-1 py-3 px-3 rounded-lg flex items-center justify-center gap-1 ${
            isOnline ? buttonClass() : disabledButtonClass()
          }`}
        >
          <span className="text-sm font-medium">Close</span>
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default CurtainControl;
